import { Instant, LocalDate, LocalDateTime, Month } from "@js-joda/core";
import * as React from "react";
import { Page } from "../activities/AppPage";
import { API } from "../API";
import { deepEqual } from "../deep-equal";
import { isLoading, Loading, loading } from "../Loading";
import * as messages from "../messages";
import {
  CaregiverImportedVisitDetails,
  PatientTaskInstance,
  PatientTaskInstanceVisitUnion,
} from "../messages";
import {
  CaregiverId,
  ImportedVisitId,
  PatientTaskInstanceId,
  VisitInstanceId,
} from "../messages/ObjectIds";
import {
  CaregiverVisitEditRequest,
  ScheduledVisitEditParams,
} from "../messages/ScheduledVisitEditRequest";
import {
  CaregiverImportedVisitDetailsTaskUnion,
  ClockedInStatus,
  ClockInOutCallType,
  VisitInstanceClockinClockoutRequestParams,
  VisitInstanceDetails,
} from "../messages/Visit";
import { STRINGS } from "../strings";
import {
  ScheduledVisitDetailsAndRequests,
  ScheduledVisitDetailsAndRequestsTaskUnion,
} from "../system/AppState/AppScheduledVisitsState";
import { translate } from "../translate";
import { List } from "../utils/List";
import { CicoModalEntity } from "../utils/visitUtils";
import CaregiverScheduleScreen from "./CaregiverScheduleScreen";
import ScreenPlaceholder from "./ScreenPlaceholder";
import { UseContactSupportReturnType } from "./useContactSupport";
import { ScheduleViewState } from "./VisitsScreen";

export interface AssignedVisitsListProps {
  caregiverId: CaregiverId;
  isCaregiverOfMedflyt: boolean;
  clockedInStatus: ClockedInStatus;
  importedVisits: List<messages.CaregiverImportedVisitDetails>;
  contactSupportProps: UseContactSupportReturnType;
  onRequestNavigate: (page: Page) => void;
  onScheduledVisitClick: (visitInstanceId: VisitInstanceId) => void;
  onImportedVisitClick: (importedVisitId: ImportedVisitId, patientName: string) => void;
  onClickTask: (
    patientTaskInstanceId: PatientTaskInstanceId,
    initialScheduleDateTimePickerOpen: boolean
  ) => void;
  rescheduleRequests: List<CaregiverVisitEditRequest>;
  patientTasksInstances: List<PatientTaskInstance>;
  visitInstances: List<VisitInstanceDetails>;
  scheduleView: ScheduleViewState;
  scheduleViewMonth: Month;
  scheduleViewYear: number;
  onRequestAvailableVisits: () => void;
  onClock: (
    visitInstanceId: VisitInstanceId,
    clockInOutCallType: ClockInOutCallType,
    visitInstanceClockinClockoutRequestParams: VisitInstanceClockinClockoutRequestParams
  ) => Promise<boolean>;
  onClickStartNow: (entity: CicoModalEntity) => void;
  onUploadVerificationSelfie: (
    visitInstanceId: VisitInstanceId,
    imageBase64: string,
    clockType: ClockInOutCallType
  ) => Promise<void>;
  onRequestVisitAdjustmentCheck: API["getCaregiverCanAdjustVisitInstanceTimes"];
  onClickNextMonth: () => void;
  onCheckIfDocumentsSubmitted: (visitId: VisitInstanceId) => Promise<boolean>;
  onCaregiverMarkVisitAsMissed: API["caregiverMarkVisitAsMissed"];
}

export class AssignedVisitsAndTasksList extends React.Component<AssignedVisitsListProps> {
  constructor(props: AssignedVisitsListProps) {
    super(props);
  }

  shouldComponentUpdate(nextProps: AssignedVisitsListProps) {
    return (
      this.props.scheduleView !== nextProps.scheduleView ||
      this.props.scheduleViewYear !== nextProps.scheduleViewYear ||
      this.props.scheduleViewMonth !== nextProps.scheduleViewMonth ||
      this.props.patientTasksInstances !== nextProps.patientTasksInstances ||
      !deepEqual(this.props.importedVisits, nextProps.importedVisits) ||
      !deepEqual(this.props.visitInstances, nextProps.visitInstances) ||
      !deepEqual(this.props.rescheduleRequests, nextProps.rescheduleRequests)
    );
  }

  render() {
    if (
      this.props.visitInstances.length() === 0 &&
      this.props.importedVisits.length() === 0 &&
      this.props.patientTasksInstances.length() === 0
    ) {
      return (
        <ScreenPlaceholder
          headline={translate(STRINGS.THERE_ARE_NO_ASSIGNED_CASES)}
          subheadline={translate(STRINGS.FIND_MORE_CASES_IN_THE_AVAILABLE_VISITS_PAGE)}
          illustration="NoAssignedCases"
          buttonText={translate(STRINGS.LETS_FIND_YOUR_FIRST_CASE)}
          onClick={this.props.onRequestAvailableVisits}
        />
      );
    } else {
      const patientTasks = this.props.patientTasksInstances.map((x) => ({ ...x, shown: false }));
      return (
        <CaregiverScheduleScreen
          caregiverId={this.props.caregiverId}
          isCaregiverOfMedflyt={this.props.isCaregiverOfMedflyt}
          clockedInStatus={this.props.clockedInStatus}
          month={this.props.scheduleViewMonth}
          year={this.props.scheduleViewYear}
          scheduledAndImportedVisits={this.convertData(
            this.props.visitInstances,
            this.props.importedVisits,
            this.props.patientTasksInstances
          )}
          patientTaskInstances={patientTasks}
          contactSupportProps={this.props.contactSupportProps}
          onRequestNavigate={this.props.onRequestNavigate}
          onScheduledVisitClick={this.props.onScheduledVisitClick}
          onImportedVisitClick={this.props.onImportedVisitClick}
          onClickTask={this.props.onClickTask}
          onClock={this.props.onClock}
          onClickStartNow={this.props.onClickStartNow}
          onUploadVerificationSelfie={this.props.onUploadVerificationSelfie}
          onRequestVisitAdjustmentCheck={this.props.onRequestVisitAdjustmentCheck}
          onClickNextMonth={this.props.onClickNextMonth}
          onCheckIfDocumentsSubmitted={this.props.onCheckIfDocumentsSubmitted}
          onCaregiverMarkVisitAsMissed={this.props.onCaregiverMarkVisitAsMissed}
        />
      );
    }
  }

  convertData(
    scheduledVisits: List<VisitInstanceDetails>,
    importedVisits: List<CaregiverImportedVisitDetails>,
    patientTaskInstances: List<PatientTaskInstance>
  ): LocalDateMultiMap<
    | ScheduledVisitDetailsAndRequestsTaskUnion
    | CaregiverImportedVisitDetailsTaskUnion
    | PatientTaskInstanceVisitUnion
  > {
    let toParse:
      | List<CaregiverImportedVisitDetailsTaskUnion | ScheduledVisitDetailsAndRequestsTaskUnion>
      | Loading = getEditRequestsForScheduledVisits(scheduledVisits, this.props.rescheduleRequests);
    if (isLoading(toParse)) {
      throw new Error("TODO???");
    }

    toParse = toParse.concat(importedVisits.map((x) => ({ ...x, union_type: "IMPORTED_VISIT" })));

    const convertedVisits = new LocalDateMultiMap<
      | ScheduledVisitDetailsAndRequestsTaskUnion
      | CaregiverImportedVisitDetailsTaskUnion
      | PatientTaskInstanceVisitUnion
    >();

    for (const visit of toParse.sortBy((v) => this.getStartTimeUTC(v))) {
      convertedVisits.add(this.getStartTimeLocal(visit).toLocalDate(), visit);
    }

    for (const task of patientTaskInstances) {
      if (task.type === "COMPLETED" || task.type === "SCHEDULED") {
        const taskToAdd: PatientTaskInstanceVisitUnion = {
          ...task,
          union_type: "TASK",
        };
        convertedVisits.add(
          LocalDateTime.ofInstant(task.scheduleDateTime).toLocalDate(),
          taskToAdd
        );
      }
    }

    convertedVisits.sort();
    return convertedVisits;
  }

  getStartTimeUTC(
    visit: ScheduledVisitDetailsAndRequests | CaregiverImportedVisitDetails
  ): Instant {
    const visitStartTimeUTC =
      (visit as any).scheduledVisitDetails === undefined
        ? (visit as CaregiverImportedVisitDetails).startTimeUTC
        : (visit as ScheduledVisitDetailsAndRequests).scheduledVisitDetails.startTimeUTC;

    return visitStartTimeUTC;
  }

  getStartTimeLocal(
    visit: ScheduledVisitDetailsAndRequests | CaregiverImportedVisitDetails
  ): LocalDateTime {
    const visitStartTimeLocal =
      (visit as any).scheduledVisitDetails === undefined
        ? (visit as CaregiverImportedVisitDetails).startTimeLocal
        : (visit as ScheduledVisitDetailsAndRequests).scheduledVisitDetails.startTimeLocal;

    return visitStartTimeLocal;
  }
}

export class LocalDateMultiMap<T> {
  get(key: LocalDate): T[] {
    const v = this.internalMap.get(LocalDateMultiMap.keyToString(key));
    if (v !== undefined) {
      return v;
    } else {
      return [];
    }
  }

  getBetween(start: LocalDate, end: LocalDate): T[] {
    const result: T[] = [];
    this.internalMap.forEach((value: T[], key: string) => {
      const localDate = LocalDate.parse(key);
      if (localDate.isAfter(start) && (localDate.isBefore(end) || localDate.equals(end))) {
        result.push(...value);
      }
    });
    return result;
  }

  size(): number {
    return this.internalMap.size;
  }

  add(key: LocalDate, value: T): void {
    const v = this.internalMap.get(LocalDateMultiMap.keyToString(key));
    if (v !== undefined) {
      v.push(value);
    } else {
      this.internalMap.set(LocalDateMultiMap.keyToString(key), [value]);
    }
  }

  forEach(callback: (value: T[], key: LocalDate) => void) {
    this.internalMap.forEach((value: T[], key: string) => {
      callback(value, LocalDateMultiMap.keyFromString(key));
    });
  }

  private internalMap: Map<string, T[]> = new Map<string, T[]>();

  private static keyToString(key: LocalDate): string {
    return key.toString();
  }

  private static keyFromString(str: string): LocalDate {
    return LocalDate.parse(str);
  }

  sort() {
    this.internalMap = new Map([...this.internalMap].sort());
  }
}

function getEditRequestsForScheduledVisits(
  scheduledVisitDetails: List<VisitInstanceDetails> | Loading,
  editRequests: List<CaregiverVisitEditRequest> | Loading
): List<ScheduledVisitDetailsAndRequestsTaskUnion> | Loading {
  if (isLoading(scheduledVisitDetails) || isLoading(editRequests)) {
    return loading();
  }
  return scheduledVisitDetails.map((schedVisit): ScheduledVisitDetailsAndRequestsTaskUnion => {
    return {
      union_type: "SCHEDULED_VISIT",
      scheduledVisitDetails: schedVisit,
      editRequestParams: lookupEditRequestParamsForScheduledVisit(editRequests, schedVisit.id),
    };
  });
}
export function lookupEditRequestParamsForScheduledVisit(
  editRequests: List<CaregiverVisitEditRequest>,
  visitInstanceId: VisitInstanceId
): ScheduledVisitEditParams | null {
  for (const request of editRequests) {
    if (request.scheduledVisitDetails.id === visitInstanceId) {
      return request.editRequestParams;
    }
  }

  return null;
}
